import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { useAuth } from '../context/AuthContext';
import { claimPurchase } from '../lib/api';
import { getStoredAuthToken } from '../lib/authToken';

const MIN_LEN = 10;

export function RegisterPage() {
  const [params] = useSearchParams();
  const claimSession = params.get('claim_session') ?? '';
  const navigate = useNavigate();
  const { register } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [signupSource, setSignupSource] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (password.length < MIN_LEN) {
      setErr(`Wachtwoord minimaal ${MIN_LEN} tekens.`);
      return;
    }
    setBusy(true);
    try {
      const r = await register(email, password, signupSource || undefined);
      if (!r.ok) {
        setErr(r.message);
        return;
      }
      if (claimSession.startsWith('cs_')) {
        const t = getStoredAuthToken();
        if (t) {
          const c = await claimPurchase(t, claimSession);
          if (!c.ok) {
            setErr(c.message ?? c.error);
            return;
          }
        }
      }
      navigate('/account', { replace: true });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Seo
        path="/registreren"
        title="Registreren"
        description="Maak een ComfortTool™-account aan om aankopen te beheren en rapporten te genereren."
        keywords="ComfortTool registreren, account aanmaken"
      />
      <div className="mx-auto max-w-md px-4 py-12">
        <h1 className="text-2xl font-bold text-ct-text">Account aanmaken</h1>
        {claimSession ? (
          <p className="mt-2 text-sm text-ct-muted">
            Gebruik het <strong className="text-ct-text">zelfde e-mailadres</strong> als bij de betaling om je aankoop te
            kunnen koppelen.
          </p>
        ) : (
          <p className="mt-2 text-sm text-ct-muted">
            Na je eerste betaling kun je hier ook inloggen om aankopen te beheren.
          </p>
        )}
        <form className="mt-8 space-y-4" onSubmit={(e) => void onSubmit(e)}>
          <div>
            <label className="block text-sm font-medium text-ct-text" htmlFor="reg-email">
              E-mail
            </label>
            <input
              id="reg-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-lg border border-ct-border bg-ct-bg px-3 py-2 text-ct-text"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ct-text" htmlFor="reg-source">
              Hoorde je van ons via (optioneel)
            </label>
            <select
              id="reg-source"
              value={signupSource}
              onChange={(e) => setSignupSource(e.target.value)}
              className="mt-1 w-full rounded-lg border border-ct-border bg-ct-bg px-3 py-2 text-ct-text"
            >
              <option value="">— niet gekozen —</option>
              <option value="zoekmachine">Zoekmachine</option>
              <option value="social">Social media</option>
              <option value="aanbeveling">Aanbeveling / collega</option>
              <option value="prijzen">Prijzenpagina</option>
              <option value="anders">Anders</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-ct-text" htmlFor="reg-password">
              Wachtwoord (min. {MIN_LEN} tekens)
            </label>
            <input
              id="reg-password"
              type="password"
              autoComplete="new-password"
              required
              minLength={MIN_LEN}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-lg border border-ct-border bg-ct-bg px-3 py-2 text-ct-text"
            />
          </div>
          {err ? (
            <p className="text-sm text-red-400" role="alert">
              {err}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-xl bg-ct-accent py-2.5 text-sm font-bold text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {busy ? 'Bezig…' : 'Account aanmaken'}
          </button>
        </form>
        <p className="mt-6 text-center text-sm text-ct-muted">
          Al een account?{' '}
          <Link
            to={claimSession ? `/inloggen?claim_session=${encodeURIComponent(claimSession)}` : '/inloggen'}
            className="font-semibold text-ct-accent hover:underline"
          >
            Inloggen
          </Link>
        </p>
      </div>
    </>
  );
}
